/**
 * Single source of truth for every language Statlign can be served in.
 *
 * Architecture (see the language-expansion plan):
 *   - One canonical data record → many localized URLs.
 *   - English is served at the ROOT (`/predictions`); every other enabled
 *     language is served under a path prefix (`/es/predictions`, `/ar/...`).
 *   - Everything downstream (routing basename, `<html lang>`/`dir`, hreflang
 *     sitemap generation, the language switcher, translation tooling) reads
 *     from this catalog so locales are added in exactly one place.
 */

export interface LocaleDef {
  /** BCP-47 short code — also the URL prefix (except English which is root). */
  code: string;
  /** English name (for admin/docs). */
  englishName: string;
  /** Endonym shown in the language switcher. */
  nativeName: string;
  /** Text direction — Arabic is the only RTL locale in the launch set. */
  dir: "ltr" | "rtl";
  /** hreflang value emitted in <link rel="alternate"> and sitemaps. */
  hreflang: string;
  /** BCP-47 tag for Intl (dates/numbers). */
  intlLocale: string;
  /** Whether the locale is live (served + indexed). "Later" tier is false. */
  enabled: boolean;
}

/**
 * Launch set + "later" tier. English MUST stay first and enabled — it is the
 * canonical source every other locale is translated from.
 */
export const LOCALES: LocaleDef[] = [
  {
    code: "en",
    englishName: "English",
    nativeName: "English",
    dir: "ltr",
    hreflang: "en",
    intlLocale: "en",
    enabled: true,
  },
  {
    code: "es",
    englishName: "Spanish",
    nativeName: "Español",
    dir: "ltr",
    hreflang: "es",
    intlLocale: "es",
    enabled: true,
  },
  {
    code: "pt",
    englishName: "Portuguese",
    nativeName: "Português",
    dir: "ltr",
    hreflang: "pt",
    intlLocale: "pt",
    enabled: true,
  },
  {
    code: "fr",
    englishName: "French",
    nativeName: "Français",
    dir: "ltr",
    hreflang: "fr",
    intlLocale: "fr",
    enabled: true,
  },
  {
    code: "de",
    englishName: "German",
    nativeName: "Deutsch",
    dir: "ltr",
    hreflang: "de",
    intlLocale: "de",
    enabled: true,
  },
  {
    code: "it",
    englishName: "Italian",
    nativeName: "Italiano",
    dir: "ltr",
    hreflang: "it",
    intlLocale: "it",
    enabled: true,
  },
  {
    code: "ar",
    englishName: "Arabic",
    nativeName: "العربية",
    dir: "rtl",
    hreflang: "ar",
    intlLocale: "ar",
    enabled: true,
  },
  {
    code: "ru",
    englishName: "Russian",
    nativeName: "Русский",
    dir: "ltr",
    hreflang: "ru",
    intlLocale: "ru",
    enabled: true,
  },
  {
    code: "ja",
    englishName: "Japanese",
    nativeName: "日本語",
    dir: "ltr",
    hreflang: "ja",
    intlLocale: "ja",
    enabled: true,
  },
  {
    code: "ko",
    englishName: "Korean",
    nativeName: "한국어",
    dir: "ltr",
    hreflang: "ko",
    intlLocale: "ko",
    enabled: true,
  },
  {
    code: "hi",
    englishName: "Hindi",
    nativeName: "हिन्दी",
    dir: "ltr",
    hreflang: "hi",
    intlLocale: "hi",
    enabled: true,
  },
  {
    code: "tr",
    englishName: "Turkish",
    nativeName: "Türkçe",
    dir: "ltr",
    hreflang: "tr",
    intlLocale: "tr",
    enabled: true,
  },
  {
    code: "id",
    englishName: "Indonesian",
    nativeName: "Bahasa Indonesia",
    dir: "ltr",
    hreflang: "id",
    intlLocale: "id",
    enabled: true,
  },
  {
    code: "pl",
    englishName: "Polish",
    nativeName: "Polski",
    dir: "ltr",
    hreflang: "pl",
    intlLocale: "pl",
    enabled: true,
  },
  {
    code: "nl",
    englishName: "Dutch",
    nativeName: "Nederlands",
    dir: "ltr",
    hreflang: "nl",
    intlLocale: "nl",
    enabled: true,
  },
  {
    code: "el",
    englishName: "Greek",
    nativeName: "Ελληνικά",
    dir: "ltr",
    hreflang: "el",
    intlLocale: "el",
    enabled: true,
  },
  {
    code: "sv",
    englishName: "Swedish",
    nativeName: "Svenska",
    dir: "ltr",
    hreflang: "sv",
    intlLocale: "sv",
    enabled: true,
  },
  {
    code: "fil",
    englishName: "Filipino",
    nativeName: "Filipino",
    dir: "ltr",
    hreflang: "fil",
    intlLocale: "fil",
    enabled: true,
  },
  {
    code: "am",
    englishName: "Amharic",
    nativeName: "አማርኛ",
    dir: "ltr",
    hreflang: "am",
    intlLocale: "am",
    enabled: true,
  },
  // Chinese variants — Simplified for Mainland China, Traditional for Taiwan/HK.
  {
    code: "zh",
    englishName: "Chinese (Simplified)",
    nativeName: "简体中文",
    dir: "ltr",
    hreflang: "zh-Hans",
    intlLocale: "zh-Hans",
    enabled: true,
  },
  {
    code: "zh-TW",
    englishName: "Chinese (Traditional)",
    nativeName: "繁體中文",
    dir: "ltr",
    hreflang: "zh-Hant",
    intlLocale: "zh-Hant",
    enabled: true,
  },
  { code: "th", englishName: "Thai", nativeName: "ไทย", dir: "ltr", hreflang: "th", intlLocale: "th", enabled: false },
  {
    code: "vi",
    englishName: "Vietnamese",
    nativeName: "Tiếng Việt",
    dir: "ltr",
    hreflang: "vi",
    intlLocale: "vi",
    enabled: true,
  },
  {
    code: "ms",
    englishName: "Malay",
    nativeName: "Bahasa Melayu",
    dir: "ltr",
    hreflang: "ms",
    intlLocale: "ms",
    enabled: true,
  },
];

export const DEFAULT_LOCALE = "en";

/** Enabled locales only — what the site actually serves and indexes. */
export const ENABLED_LOCALES = LOCALES.filter((l) => l.enabled);

/** Enabled non-English locales — the ones that get a URL prefix. */
export const PREFIXED_LOCALES = ENABLED_LOCALES.filter((l) => l.code !== DEFAULT_LOCALE);

/**
 * Locales submitted to search engines. English only for now: the domain's
 * authority is too low to carry 30+ machine-translated copies, which search
 * engines treat as scaled low-value content. Other locales still serve
 * visitors but are `noindex,follow` and left out of sitemaps/hreflang. Add a
 * code here to re-open a language once its content is reviewed.
 */
export const INDEXED_LOCALE_CODES: readonly string[] = ["en"];
export const INDEXED_LOCALES = ENABLED_LOCALES.filter((l) => INDEXED_LOCALE_CODES.includes(l.code));

/**
 * Translated pages open to search engines. Empty by owner decision: only the
 * English statlign.com pages are indexed and listed in sitemaps; every
 * translated URL is noindex with an English canonical and no hreflang.
 */
export const TRANSLATED_INDEXABLE_PATHS: readonly string[] = [];
export function isTranslationIndexable(code: string | null | undefined, path: string): boolean {
  if (isLocaleIndexed(code)) return true;
  const p = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return TRANSLATED_INDEXABLE_PATHS.includes(p || "/") && ENABLED_LOCALES.some((l) => l.code === code);
}
export function isLocaleIndexed(code: string | null | undefined): boolean {
  return INDEXED_LOCALE_CODES.includes(code || DEFAULT_LOCALE);
}

export const ENABLED_CODES = ENABLED_LOCALES.map((l) => l.code);

const BY_CODE = new Map(LOCALES.map((l) => [l.code, l]));

export function getLocale(code: string | null | undefined): LocaleDef {
  return (code && BY_CODE.get(code)) || BY_CODE.get(DEFAULT_LOCALE)!;
}

export function isEnabledLocale(code: string | null | undefined): boolean {
  return !!code && ENABLED_CODES.includes(code);
}

/**
 * Split a pathname into its (optional) locale prefix and the remaining
 * root-relative path. English has no prefix, so `/predictions` → {en, /predictions}
 * and `/es/predictions` → {es, /predictions}. Unknown/disabled prefixes are
 * treated as regular path segments (locale falls back to English).
 */
export function parsePath(pathname: string): { locale: string; path: string } {
  const segments = pathname.replace(/^\/+/, "").split("/");
  const first = segments[0];
  if (first && first !== DEFAULT_LOCALE && isEnabledLocale(first)) {
    const rest = "/" + segments.slice(1).join("/");
    return { locale: first, path: rest === "/" ? "/" : rest.replace(/\/$/, "") };
  }
  return { locale: DEFAULT_LOCALE, path: pathname };
}

/** Router basename for a locale: "" for English, "/es" etc. otherwise. */
export function basenameForLocale(code: string): string {
  return !code || code === DEFAULT_LOCALE ? "" : `/${code}`;
}

/** Build a full localized URL path from a root-relative path. */
export function localizedPath(code: string, path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return code === DEFAULT_LOCALE ? clean : `/${code}${clean === "/" ? "" : clean}`;
}

/**
 * Pick the best enabled locale from a browser Accept-Language style list.
 * Falls back to English. Exact match first, then base-language match.
 */
export function matchBrowserLocale(candidates: readonly string[]): string {
  for (const raw of candidates) {
    const lower = raw.toLowerCase();
    if (isEnabledLocale(lower)) return lower;
    const base = lower.split("-")[0]!;
    if (isEnabledLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}
